import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { ReqImg } from "./portal/PortalRequests";
import { useAuth } from "../context/AuthContext";
import { Hammer, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_STYLE = {
  urgent: "bg-red-600 text-white", high: "bg-amber-500 text-white",
  normal: "bg-stone-100 text-stone-600 border border-stone-200", low: "bg-stone-100 text-stone-400 border border-stone-200",
};

export default function ClientRequests() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("open");

  const load = () => api.get("/work-requests").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const complete = async (r) => {
    try {
      await api.put(`/work-requests/${r.id}/complete`);
      toast.success("Marked complete");
      load();
    } catch (e) {
      toast.error("Could not mark complete");
    }
  };

  const shown = (rows || []).filter((r) => r.status === tab);

  return (
    <div className="space-y-5" data-testid="client-requests-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Client Requests</h1>
        <p className="text-stone-500 text-sm mt-1">Work requested by clients through the portal{isAdmin ? " · only admins can mark complete" : " · read-only (admin marks complete)"}</p>
      </div>

      <div className="flex gap-0 bg-white border border-stone-200 w-fit">
        {["open", "approved", "complete"].map((s) => (
          <button key={s} data-testid={`requests-tab-${s}`} onClick={() => setTab(s)}
            className={`h-10 px-5 text-xs font-bold uppercase ${tab === s ? "bg-[#1F3D34] text-white" : "text-stone-600"}`}>
            {s} ({(rows || []).filter((r) => r.status === s).length})
          </button>
        ))}
      </div>

      <div className="space-y-2" data-testid="client-requests-list">
        {shown.map((r) => (
          <div key={r.id} data-testid={`client-request-${r.id}`} className="bg-white border border-stone-200 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <Hammer size={18} className="text-[#7C9673] mt-0.5 shrink-0" />
                <div>
                  <div className="font-bold">{r.title}</div>
                  {r.details && <div className="text-sm text-stone-500 mt-0.5 whitespace-pre-wrap">{r.details}</div>}
                  <div className="text-[10px] text-stone-400 mt-1">
                    {r.requested_by} · {(r.ranch_numbers || []).join(", ")} · {(r.created_at || "").slice(0, 10)}
                    {r.status === "approved" ? ` · approved ${(r.approved_at || "").slice(0, 10)} by ${r.approved_by} — sent to Work List` : ""}
                    {r.status === "complete" ? ` · completed ${(r.completed_at || "").slice(0, 10)} by ${r.completed_by}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase px-2 py-1 ${PRIORITY_STYLE[r.priority] || PRIORITY_STYLE.normal}`}>{r.priority}</span>
                {r.status === "complete" ? (
                  <span className="text-[10px] font-bold uppercase px-2 py-1 bg-[#7C9673] text-white flex items-center gap-1"><CheckCircle2 size={11} /> Complete</span>
                ) : (
                  <>
                    {r.status === "approved" && <span className="text-[10px] font-bold uppercase px-2 py-1 bg-amber-100 text-amber-800 border border-amber-300">Approved · In Work List</span>}
                    {isAdmin ? (
                      <button data-testid={`complete-request-${r.id}`} onClick={() => complete(r)}
                        className="h-9 px-4 bg-[#1F3D34] text-white font-bold uppercase text-[10px] flex items-center gap-1.5">
                        <CheckCircle2 size={13} /> Mark Complete
                      </button>
                    ) : r.status === "open" ? (
                      <span className="text-[10px] font-bold uppercase px-2 py-1 bg-stone-100 text-stone-500 border border-stone-200">Open</span>
                    ) : null}
                  </>
                )}
              </div>
            </div>
            {(r.photos || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid={`request-photos-${r.id}`}>
                {r.photos.map((p) => <ReqImg key={p.id} rid={r.id} pid={p.id} className="h-24 w-24 object-cover border border-stone-200" />)}
              </div>
            )}
          </div>
        ))}
        {rows && shown.length === 0 && (
          <div className="bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm" data-testid="client-requests-empty">
            No {tab} requests
          </div>
        )}
      </div>
    </div>
  );
}
